/**
 * The Products screen's arithmetic and wording (components/Products.jsx).
 *
 * The screen sells retail products on the reader: with a pick-up, or on
 * their own with no garment. It sends product ids and quantities only. The
 * price, the sales tax and the total all come back from the server
 * (fxrybackend helper/products.js), so the figures here are for showing the
 * basket while it is being built and never for charging.
 *
 * Pure: tested in products.test.mjs.
 */

export const QTY_MAX = 20;

export const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

/** A quantity moved by one step, kept between 0 and QTY_MAX. */
export const stepQty = (current, step) =>
  Math.max(0, Math.min(QTY_MAX, (Number(current) || 0) + step));

/** What the basket counts against. A product with motifs is counted one
 *  motif at a time, because each is a different thing to hand over and the
 *  sale records which one went (fxrybackend helper/products.js). */
export const basketKey = (productId, motif = "") =>
  motif ? `${productId}::${motif}` : String(productId);

/**
 * The rows the screen can tap: one per product, or one per motif when the
 * product has them.
 *
 * A row each rather than a product with a chooser beside it, because staff
 * are holding the thing: one tap on the motif in their hand beats two taps
 * and a decision. The shelf count belongs to the product, so every row of
 * one product shows the same count.
 */
export const sellableRows = (products = []) =>
  (products || []).flatMap((product) => {
    const motifs = product.motifs || [];
    if (motifs.length === 0) {
      return [{ product, motif: "", key: basketKey(product._id, "") }];
    }
    return motifs.map((motif) => ({
      product,
      motif,
      key: basketKey(product._id, motif),
    }));
  });

/** The basket as lines, in the list's order: rows with a quantity. */
export const basketLines = (products = [], qtyByKey = {}) =>
  sellableRows(products)
    .filter((row) => (qtyByKey[row.key] || 0) > 0)
    .map(({ product, motif, key }) => ({
      key,
      product_id: product._id,
      motif,
      name: product.name,
      brand: product.brand || "",
      qty: qtyByKey[key],
      unit_price_cents: product.price_cents,
      amount_cents: product.price_cents * qtyByKey[key],
      // Natalie's named envelope: cut and stitched to order, so the sale
      // needs the name and nothing comes off a shelf.
      needs_a_name: product.gift_envelope_named === true,
    }));

/** Longest name Natalie will stitch, the same limit as the gift card screen
 *  (fxrypopup/src/utils/giftCertificate.js). */
export const STITCHED_NAME_MAX = 24;

/** Is anything in the basket stitched to order? */
export const needsAName = (lines = []) =>
  lines.some((line) => line.needs_a_name);

export const basketSubtotal = (lines = []) =>
  lines.reduce((sum, line) => sum + line.amount_cents, 0);

/**
 * What the server is asked to sell. No prices are sent.
 *
 * The counter used to be asked whether this was with a pick-up, which tagged
 * the sale `at_pickup` and fed one figure on the Products page. Calley, 31
 * Oct 2026: nobody cares, and it read like a question about where the pin
 * was going when the sale was always its own sale either way. It was two
 * taps and a blocked Pay button for a number nobody was reading, so the
 * question is gone and every counter sale files as its own.
 *
 * The field itself stays on the schema, because the sales already taken
 * carry it and their record should keep saying what it said.
 */
export const saleBody = (lines, { stitchedName } = {}) => ({
  lines: lines.map(({ product_id, qty, motif }) => ({
    product_id,
    qty,
    ...(motif ? { motif } : {}),
  })),
  // Only sent when something in the sale is stitched to order; the server
  // drops it otherwise (helper/products.js saleLines).
  ...(needsAName(lines)
    ? { name_on_envelope: String(stitchedName || "").trim() }
    : {}),
});

/**
 * Why the sale can't be paid yet, or null.
 *
 * Only two things can hold a sale up now: an empty basket, and a stitched
 * envelope with no name to stitch on it. Both are things the sale cannot be
 * made without.
 */
export const payBlocker = (lines, stitchedName = "") => {
  if (!lines.length) return "Add a product.";
  if (needsAName(lines)) {
    const name = String(stitchedName || "").trim();
    if (!name) return "Type the name to stitch on the envelope.";
    if (name.length > STITCHED_NAME_MAX) {
      return `The stitched name can be up to ${STITCHED_NAME_MAX} characters.`;
    }
    // Two would need two names, and the sale carries one.
    if (lines.some((line) => line.needs_a_name && line.qty > 1)) {
      return "Sell one named envelope at a time, so each gets its own name.";
    }
  }
  return null;
};

/** The small note under a product: how many the count says are left. */
export const stockNote = (product) => {
  // Made to order: there is no shelf, so a count would be a lie either way.
  if (product.stock_state === "made_to_order") return "Made to order";
  // The count is the product's, not the motif's, so on a row per motif it
  // says so: six envelope rows all reading "13 left" would each look like
  // thirteen of that motif.
  if (product.stock_state === "ok") {
    return (product.motifs || []).length > 0
      ? `${product.stock} left in all`
      : `${product.stock} left`;
  }
  // Zero or below on the count, but it may be in the customer's hand: the
  // sale is allowed and the count gets fixed on the Products page.
  return "None on the count";
};

/** "Soak · Wool wash", or the name alone. */
export const productTitle = (product) =>
  product.brand ? `${product.brand} · ${product.name}` : product.name;

/** A sale line as the basket and the receipt show it: the product, then
 *  the motif when it has one. The server sends the motif back on the paid
 *  lines too, so the receipt says which one went. */
export const lineLabel = (line) =>
  line?.motif ? `${line.name} · ${line.motif}` : line?.name || "";

/** The title of one tappable row: the product, then the motif when it has
 *  one. "FXRY · Beaded Pin · Snake". */
export const rowTitle = (product, motif = "") =>
  motif ? `${productTitle(product)} · ${motif}` : productTitle(product);

/**
 * The stages at which a product sale can still be called off.
 *
 * Up to the moment the card is read, Cancel stops the reader and the sale is
 * off with nothing charged. Once `processPayment` has the card, Stripe
 * decides whether the money moves, so from there the only honest way back is
 * a refund: the same line the garment checkout draws
 * (components/TerminalCheckoutForm.jsx).
 */
export const CANCELLABLE_STAGES = ["connecting", "collecting"];

/** Whether the Cancel button shows, and whether it is allowed to act. */
export const canCancelSale = (stage) => CANCELLABLE_STAGES.includes(stage);
